import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AttendanceCorrectionsAdmin from "@/components/AttendanceCorrectionsAdmin";

export default function Approvals() {
  const qc = useQueryClient();
  const { isSuperAdmin } = useAuth();

  const { data: pending, isLoading } = useQuery({
    queryKey: ["pendingChanges"], enabled: isSuperAdmin,
    queryFn: async () => (await api.get("/hr-changes/pending")).data,
  });
  const { data: requests } = useQuery({
    queryKey: ["changeRequests"],
    queryFn: async () => (await api.get("/change-requests", { params: { status: "Pending" } })).data,
  });
  // Count only what this user can act on: Super Admin sees every open request,
  // HR sees employee requests still waiting for HR review.
  const { data: attOpen } = useQuery({
    queryKey: ["attCorrectionsCount", isSuperAdmin],
    queryFn: async () => (await api.get("/attendance/corrections", { params: { status: isSuperAdmin ? "open" : "Pending" } })).data,
  });

  const decideChange = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/hr-changes/${id}/decide`, { action, remarks: action === "approve" ? "Approved" : "Rejected" })).data,
    onSuccess: () => { toast.success("Decision recorded"); qc.invalidateQueries({ queryKey: ["pendingChanges"] }); qc.invalidateQueries({ queryKey: ["employee"] }); qc.invalidateQueries({ queryKey: ["hrhist"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const decideReq = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/change-requests/${id}/decide`, { action, remarks: action === "approve" ? "Approved — HR will update" : "Rejected" })).data,
    onSuccess: () => { toast.success("Decision recorded"); qc.invalidateQueries({ queryKey: ["changeRequests"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader overline="Governance" title="Approvals" icon={ClipboardCheck}
        subtitle="Approve salary/grade & employment-status changes, employee correction requests and attendance corrections." />
      <Tabs defaultValue={isSuperAdmin ? "changes" : "requests"}>
        <TabsList className="bg-slate-100">
          {isSuperAdmin && <TabsTrigger value="changes" data-testid="tab-changes">HR Change Approvals ({pending?.length || 0})</TabsTrigger>}
          <TabsTrigger value="requests" data-testid="tab-requests">Correction Requests ({requests?.length || 0})</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-att-corrections">Attendance Corrections ({attOpen?.length || 0})</TabsTrigger>
        </TabsList>

        {isSuperAdmin && (
          <TabsContent value="changes" className="mt-6">
            {isLoading ? <Loading /> : (pending || []).length === 0 ? (
              <EmptyState icon={ClipboardCheck} title="No pending approvals" hint="Grade/salary and employment-status changes needing your approval appear here." />
            ) : (
              <Card className="border-slate-200 shadow-none overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="overline text-slate-500">Employee</TableHead>
                    <TableHead className="overline text-slate-500">Field</TableHead>
                    <TableHead className="overline text-slate-500">Old → New</TableHead>
                    <TableHead className="overline text-slate-500">Effective</TableHead>
                    <TableHead className="overline text-slate-500">Reason</TableHead>
                    <TableHead className="text-right overline text-slate-500">Decision</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {pending.map((c) => (
                      <TableRow key={c.id} className="table-row-i" data-testid={`pending-${c.id}`}>
                        <TableCell className="font-medium text-navy">{c.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{c.employee_code}</span></TableCell>
                        <TableCell className="text-slate-600">{c.field_type}</TableCell>
                        <TableCell className="text-slate-600">{c.old_display_value || c.old_value || "—"} → <span className="text-navy font-medium">{c.display_value || c.new_value}</span></TableCell>
                        <TableCell className="text-slate-600">{c.effective_from}</TableCell>
                        <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px] whitespace-normal break-words">{c.reason}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decideChange.mutate({ id: c.id, action: "approve" })} data-testid={`approve-${c.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decideChange.mutate({ id: c.id, action: "reject" })} data-testid={`reject-${c.id}`}><XCircle className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        )}

        <TabsContent value="requests" className="mt-6">
          {(requests || []).length === 0 ? (
            <EmptyState icon={ClipboardCheck} title="No correction requests" hint="Employee-submitted correction requests appear here for HR review." />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Field</TableHead>
                  <TableHead className="overline text-slate-500">Requested</TableHead>
                  <TableHead className="overline text-slate-500">Reason</TableHead>
                  <TableHead className="text-right overline text-slate-500">Decision</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {requests.map((r) => (
                    <TableRow key={r.id} className="table-row-i" data-testid={`req-${r.id}`}>
                      <TableCell className="font-medium text-navy">{r.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{r.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{r.field}</TableCell>
                      <TableCell className="text-slate-600">{r.current_value || "—"} → <span className="text-navy font-medium">{r.requested_value}</span></TableCell>
                      <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px] whitespace-normal break-words">{r.reason}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decideReq.mutate({ id: r.id, action: "approve" })} data-testid={`req-approve-${r.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decideReq.mutate({ id: r.id, action: "reject" })} data-testid={`req-reject-${r.id}`}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="attendance" className="mt-6"><AttendanceCorrectionsAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
